<template>
  <section class="shure-monthly-page">
    <div class="page-header">
      <div>
        <h1>Reports</h1>
        <p class="page-copy">
          Run each report once per selected period, cache the result, and review completion status before exporting.
        </p>
      </div>

      <div class="page-actions">
        <button class="btn btn-primary" type="button" :disabled="statusLoading" @click="loadStatuses">
          {{ statusLoading ? 'Loading...' : 'Refresh Status' }}
        </button>
        <button class="btn btn-outline-primary" type="button" :disabled="runningMissing || !hasMissingConfiguredReports" @click="runMissingReports">
          {{ runningMissing ? 'Running...' : 'Run Missing Reports' }}
        </button>
        <button class="btn btn-success" type="button" :disabled="reportLoading || exporting || !allReportsReady" @click="exportWorkbook">
          {{ exporting ? 'Exporting...' : 'Download Workbook' }}
        </button>
      </div>
    </div>

    <div class="filter-card">
      <div class="filter-grid">
        <div class="filter-field">
          <label class="form-label" for="range-mode">Range Mode</label>
          <select id="range-mode" v-model="rangeMode" class="form-select" :disabled="statusLoading || reportLoading">
            <option value="yesterday">Yesterday</option>
            <option value="last_month">Last Month</option>
            <option value="month">Specific Month</option>
            <option value="custom">Custom From/To</option>
          </select>
        </div>

        <div v-if="rangeMode === 'month'" class="filter-field">
          <label class="form-label" for="month-value">Month</label>
          <input id="month-value" v-model="selectedMonth" type="month" class="form-control" :disabled="statusLoading || reportLoading" />
        </div>

        <template v-if="rangeMode === 'custom'">
          <div class="filter-field">
            <label class="form-label" for="from-date">From</label>
            <input id="from-date" v-model="customFrom" type="date" class="form-control" :disabled="statusLoading || reportLoading" />
          </div>
          <div class="filter-field">
            <label class="form-label" for="to-date">To</label>
            <input id="to-date" v-model="customTo" type="date" class="form-control" :disabled="statusLoading || reportLoading" />
          </div>
        </template>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-if="infoMessage" class="alert alert-info">
      {{ infoMessage }}
    </div>

    <div v-if="statusSummary" class="meta-grid">
      <div class="meta-card">
        <span class="meta-label">Range</span>
        <strong>{{ rangeLabel }}</strong>
      </div>
      <div class="meta-card">
        <span class="meta-label">Configured</span>
        <strong>{{ statusSummary.configured_count }}</strong>
      </div>
      <div class="meta-card">
        <span class="meta-label">Ready</span>
        <strong>{{ statusSummary.ready_count }}</strong>
      </div>
      <div class="meta-card">
        <span class="meta-label">Total Reports</span>
        <strong>{{ statusSummary.total_count }}</strong>
      </div>
      <div class="meta-card">
        <span class="meta-label">All Ready</span>
        <strong>{{ statusSummary.all_ready ? 'Yes' : 'No' }}</strong>
      </div>
    </div>

    <div class="report-status-card">
      <div class="report-status-header">
        <div>
          <h2>Required Reports</h2>
          <p class="query-meta">
            Stored snapshots are reused for the selected period unless you explicitly refresh a report.
          </p>
        </div>
      </div>

      <div v-if="statusLoading" class="loading-state">
        Loading report status...
      </div>

      <div v-else-if="reportStatuses.length === 0" class="empty-state">
        No reports are registered.
      </div>

      <div v-else class="table-container status-table-container">
        <table class="table align-middle">
          <thead>
            <tr>
              <th>Report</th>
              <th>Status</th>
              <th>Cached At</th>
              <th>Rows</th>
              <th class="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in reportStatuses"
              :key="entry.key"
              :class="{ 'is-selected': entry.key === selectedReportKey }"
            >
              <td>
                <strong>{{ entry.label }}</strong>
                <div class="table-subtext">{{ entry.description }}</div>
                <div v-if="entry.message" class="table-warning">{{ entry.message }}</div>
              </td>
              <td>
                <span class="status-pill" :class="statusClass(entry.status)">
                  {{ statusLabel(entry.status) }}
                </span>
              </td>
              <td>{{ formatTimestamp(entry.generated_at) }}</td>
              <td>{{ entry.row_count ?? 0 }}</td>
              <td class="actions-column">
                <button class="btn btn-sm btn-outline-secondary" type="button" :disabled="entry.status === 'not_configured' || reportLoading" @click="viewReport(entry.key)">
                  View
                </button>
                <button class="btn btn-sm btn-outline-primary" type="button" :disabled="entry.status === 'not_configured' || reportLoading" @click="runReport(entry.key, false)">
                  Run
                </button>
                <button class="btn btn-sm btn-outline-dark" type="button" :disabled="entry.status === 'not_configured' || reportLoading" @click="runReport(entry.key, true)">
                  Refresh
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="paymentFeeOverviewAvailable" class="report-status-card">
      <div class="report-status-header">
        <div>
          <h2>Payment Fee Overview</h2>
          <p class="query-meta">
            CHF and PL values are converted into EUR using the cached ECB average exchange rates for the selected period.
          </p>
        </div>
      </div>

      <div class="table-container status-table-container">
        <table class="table align-middle">
          <thead>
            <tr>
              <th>Payment Provider</th>
              <th>Fees EUR</th>
              <th>Fees CHF</th>
              <th>Fees PL</th>
              <th>Fees GBP</th>
              <th>EU</th>
              <th>UK</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in paymentFeeOverviewRows" :key="row.payment_provider">
              <td><strong>{{ row.payment_provider }}</strong></td>
              <td>{{ formatAmount(row.fees_eur) }}</td>
              <td>{{ formatAmount(row.fees_chf) }}</td>
              <td>{{ formatAmount(row.fees_pl) }}</td>
              <td>{{ formatAmount(row.fees_gbp) }}</td>
              <td>{{ formatAmount(row.eu) }}</td>
              <td>{{ formatAmount(row.uk) }}</td>
              <td>{{ formatAmount(row.total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="returnsSummaryAvailable" class="report-status-card">
      <div class="report-status-header">
        <div>
          <h2>Returns Summary</h2>
          <p class="query-meta">
            Net values and item quantities grouped by return-market currency from the cached Stocky return report.
          </p>
        </div>
      </div>

      <div class="table-container status-table-container">
        <table class="table align-middle">
          <thead>
            <tr>
              <th>Currency</th>
              <th>Net Value</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in returnsSummaryRows" :key="row.currency">
              <td><strong>{{ row.currency }}</strong></td>
              <td>{{ formatAmount(row.net_value) }}</td>
              <td>{{ formatAmount(row.quantity) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="reconSummaryAvailable" class="report-status-card">
      <div class="report-status-header">
        <div>
          <h2>Recon Summary</h2>
          <p class="query-meta">
            Unique items from the cached recon report with sales and return quantities and amounts grouped by currency.
          </p>
        </div>
      </div>

      <div class="table-container status-table-container">
        <table class="table align-middle table-sm">
          <thead>
            <tr>
              <th>Item</th>
              <th>EUR Sales Qty</th>
              <th>EUR Sales Amount</th>
              <th>EUR Return Qty</th>
              <th>EUR Return Amount</th>
              <th>GBP Sales Qty</th>
              <th>GBP Sales Amount</th>
              <th>GBP Return Qty</th>
              <th>GBP Return Amount</th>
              <th>PL Sales Qty</th>
              <th>PL Sales Amount</th>
              <th>PL Return Qty</th>
              <th>PL Return Amount</th>
              <th>CHF Sales Qty</th>
              <th>CHF Sales Amount</th>
              <th>CHF Return Qty</th>
              <th>CHF Return Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in reconSummaryRows" :key="row.item">
              <td><strong>{{ row.item }}</strong></td>
              <td>{{ formatAmount(row.eur_sales_qty) }}</td>
              <td>{{ formatAmount(row.eur_sales_amount) }}</td>
              <td>{{ formatAmount(row.eur_return_qty) }}</td>
              <td>{{ formatAmount(row.eur_return_amount) }}</td>
              <td>{{ formatAmount(row.gbp_sales_qty) }}</td>
              <td>{{ formatAmount(row.gbp_sales_amount) }}</td>
              <td>{{ formatAmount(row.gbp_return_qty) }}</td>
              <td>{{ formatAmount(row.gbp_return_amount) }}</td>
              <td>{{ formatAmount(row.pl_sales_qty) }}</td>
              <td>{{ formatAmount(row.pl_sales_amount) }}</td>
              <td>{{ formatAmount(row.pl_return_qty) }}</td>
              <td>{{ formatAmount(row.pl_return_amount) }}</td>
              <td>{{ formatAmount(row.chf_sales_qty) }}</td>
              <td>{{ formatAmount(row.chf_sales_amount) }}</td>
              <td>{{ formatAmount(row.chf_return_qty) }}</td>
              <td>{{ formatAmount(row.chf_return_amount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="revenueReportAvailable" class="report-status-card">
      <div class="report-status-header">
        <div>
          <h2>Revenue Report</h2>
          <p class="query-meta">
            Derived from cached recon totals and ECB exchange rates. EU values are in EUR, UK values stay in GBP.
          </p>
        </div>
      </div>

      <div class="table-container status-table-container">
        <table class="table align-middle">
          <thead>
            <tr>
              <th>Shop Revenues</th>
              <th>EU</th>
              <th>UK</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in revenueReportRows" :key="row.shop_revenues">
              <td><strong>{{ row.shop_revenues }}</strong></td>
              <td>{{ formatAmount(row.eu) }}</td>
              <td>{{ formatAmount(row.uk) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="reportLoading" class="loading-state report-detail-state">
      Loading selected report...
    </div>

    <div v-else-if="report && !selectedReportConfigured" class="empty-state">
      <h2>Report Not Configured</h2>
      <p>{{ report.message || 'The selected report is not configured yet.' }}</p>
    </div>

    <div v-else-if="report && report.queries.length === 0" class="empty-state">
      No query results returned for the selected range.
    </div>

    <div v-else-if="report" class="query-results">
      <article v-for="query in queryResults" :key="query.key" class="query-card">
        <div class="query-card-header">
          <div>
            <h2>{{ report.report_label }}</h2>
            <p class="query-meta">
              Source: <strong>{{ query.connection }}</strong>
            </p>
          </div>
          <span class="badge text-bg-secondary">{{ query.row_count }} rows</span>
        </div>

        <div v-if="query.row_count === 0" class="query-empty">
          Query returned no rows.
        </div>

        <div v-else class="table-container">
          <table class="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th v-for="column in query.columns" :key="column">{{ column }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, rowIndex) in query.rows" :key="`${query.key}-${rowIndex}`">
                <td v-for="column in query.columns" :key="`${query.key}-${rowIndex}-${column}`">
                  {{ formatValue(row[column]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'ShureMonthlyView',
  data() {
    const defaults = this.getLastMonthRange()

    return {
      rangeMode: 'last_month',
      selectedMonth: defaults.month,
      customFrom: defaults.from,
      customTo: defaults.to,
      reportStatuses: [],
      statusSummary: null,
      paymentFeeOverview: null,
      returnsSummary: null,
      reconSummary: null,
      revenueReport: null,
      selectedReportKey: 'shure_monthly',
      report: null,
      statusLoading: false,
      reportLoading: false,
      runningMissing: false,
      exporting: false,
      error: null,
      infoMessage: ''
    }
  },
  computed: {
    rangeLabel() {
      if (this.rangeMode === 'month') {
        return this.selectedMonth || '-'
      }

      if (this.rangeMode === 'custom') {
        return `${this.customFrom || '-'} to ${this.customTo || '-'}`
      }

      if (this.rangeMode === 'yesterday') {
        return 'Yesterday'
      }

      return 'Last Month'
    },
    selectedReportConfigured() {
      const value = this.report?.configured
      return value === true || value === 1 || value === '1' || value === 'true'
    },
    queryResults() {
      return Array.isArray(this.report?.queries) ? this.report.queries : []
    },
    hasMissingConfiguredReports() {
      return this.reportStatuses.some((entry) => entry.configured && !entry.has_cache)
    },
    allReportsReady() {
      return Boolean(this.statusSummary?.all_ready)
    },
    paymentFeeOverviewRows() {
      return Array.isArray(this.paymentFeeOverview?.rows) ? this.paymentFeeOverview.rows : []
    },
    paymentFeeOverviewAvailable() {
      return this.allReportsReady && this.paymentFeeOverview?.available === true && this.paymentFeeOverviewRows.length > 0
    },
    returnsSummaryRows() {
      return Array.isArray(this.returnsSummary?.rows) ? this.returnsSummary.rows : []
    },
    returnsSummaryAvailable() {
      return this.allReportsReady && this.returnsSummary?.available === true && this.returnsSummaryRows.length > 0
    },
    reconSummaryRows() {
      return Array.isArray(this.reconSummary?.rows) ? this.reconSummary.rows : []
    },
    reconSummaryAvailable() {
      return this.reconSummary?.available === true && this.reconSummaryRows.length > 0
    },
    revenueReportRows() {
      return Array.isArray(this.revenueReport?.rows) ? this.revenueReport.rows : []
    },
    revenueReportAvailable() {
      return this.allReportsReady && this.revenueReport?.available === true && this.revenueReportRows.length > 0
    }
  },
  async mounted() {
    await this.loadStatuses()
  },
  methods: {
    getLastMonthRange() {
      const reference = new Date()
      reference.setDate(1)
      reference.setMonth(reference.getMonth() - 1)

      const year = reference.getFullYear()
      const month = String(reference.getMonth() + 1).padStart(2, '0')
      const firstDay = `${year}-${month}-01`
      const lastDayDate = new Date(year, reference.getMonth() + 1, 0)
      const lastDay = `${lastDayDate.getFullYear()}-${String(lastDayDate.getMonth() + 1).padStart(2, '0')}-${String(lastDayDate.getDate()).padStart(2, '0')}`

      return {
        month: `${year}-${month}`,
        from: firstDay,
        to: lastDay
      }
    },
    buildParams() {
      const params = {}

      if (this.rangeMode === 'month') {
        params.mode = 'month'
        params.month = this.selectedMonth
        return params
      }

      if (this.rangeMode === 'custom') {
        params.mode = 'custom'
        params.from = this.customFrom
        params.to = this.customTo
        return params
      }

      if (this.rangeMode === 'yesterday') {
        params.mode = 'yesterday'
        return params
      }

      params.mode = 'last_month'
      return params
    },
    async loadStatuses() {
      this.statusLoading = true
      this.error = null

      try {
        const response = await api.get('/api/endpoints/reports-status.php', {
          params: this.buildParams()
        })

        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Failed to load report statuses.')
        }

        const payload = response.data.data || {}
        this.reportStatuses = Array.isArray(payload.reports) ? payload.reports : []
        this.statusSummary = payload.summary || null
        this.paymentFeeOverview = payload.payment_fee_overview || null
        this.returnsSummary = payload.returns_summary || null
        this.reconSummary = payload.recon_summary || null
        this.revenueReport = payload.revenue_report || null

        if (!this.reportStatuses.some((entry) => entry.key === this.selectedReportKey)) {
          this.selectedReportKey = this.reportStatuses[0]?.key || ''
        }
      } catch (error) {
        this.reportStatuses = []
        this.statusSummary = null
        this.paymentFeeOverview = null
        this.returnsSummary = null
        this.reconSummary = null
        this.revenueReport = null
        this.error = error?.response?.data?.error || error.message || 'Failed to load report statuses.'
      } finally {
        this.statusLoading = false
      }
    },
    async viewReport(reportKey) {
      if (!reportKey) {
        return
      }

      await this.runReport(reportKey, false)
    },
    async runReport(reportKey, forceRefresh = false) {
      this.selectedReportKey = reportKey
      this.reportLoading = true
      this.error = null
      this.infoMessage = ''

      try {
        const response = await api.get('/api/endpoints/shure-monthly.php', {
          params: {
            ...this.buildParams(),
            report_key: reportKey,
            force_refresh: forceRefresh ? '1' : '0'
          }
        })

        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Failed to load report.')
        }

        const payload = response.data.data || {}
        this.report = payload.report || null
        this.infoMessage = this.report?.configured === false
          ? (this.report?.message || 'The report is not configured yet.')
          : ''

        const statusPayload = payload.status_payload || null
        if (statusPayload) {
          this.reportStatuses = Array.isArray(statusPayload.reports) ? statusPayload.reports : []
          this.statusSummary = statusPayload.summary || null
          this.paymentFeeOverview = statusPayload.payment_fee_overview || null
          this.returnsSummary = statusPayload.returns_summary || null
          this.reconSummary = statusPayload.recon_summary || null
          this.revenueReport = statusPayload.revenue_report || null
        } else {
          await this.loadStatuses()
        }
      } catch (error) {
        this.report = null
        this.error = error?.response?.data?.error || error.message || 'Failed to load report.'
      } finally {
        this.reportLoading = false
      }
    },
    async runMissingReports() {
      const missingReports = this.reportStatuses.filter((entry) => entry.configured && !entry.has_cache)
      if (missingReports.length === 0) {
        return
      }

      this.runningMissing = true
      this.error = null
      const currentSelection = this.selectedReportKey

      try {
        for (const entry of missingReports) {
          await this.runReport(entry.key, false)
        }

        if (currentSelection) {
          await this.viewReport(currentSelection)
        }
      } finally {
        this.runningMissing = false
      }
    },
    async exportWorkbook() {
      if (!this.allReportsReady) {
        this.error = 'All configured reports must be ready before the combined workbook can be exported.'
        return
      }

      this.exporting = true
      this.error = null

      try {
        const params = new URLSearchParams({
          ...this.buildParams(),
          combined: '1'
        })
        const response = await api.get(`/api/endpoints/shure-monthly-export.php?${params.toString()}`, {
          responseType: 'blob'
        })

        const blobUrl = URL.createObjectURL(response.data)
        const disposition = response.headers['content-disposition'] || ''
        const matchedFileName = disposition.match(/filename="?([^";]+)"?/i)
        const fileName = matchedFileName?.[1] || `reports-${this.report?.range?.file_suffix || this.selectedMonth || 'export'}.xlsx`
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
      } catch (error) {
        this.error = error?.response?.data?.error || error?.message || 'Failed to export workbook.'
      } finally {
        this.exporting = false
      }
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      if (typeof value === 'object') {
        return JSON.stringify(value)
      }

      return String(value)
    },
    formatTimestamp(value) {
      if (!value) {
        return '-'
      }

      return String(value).replace('T', ' ').replace('+01:00', '').replace('+00:00', '')
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      const numericValue = Number(value)
      if (Number.isNaN(numericValue)) {
        return String(value)
      }

      return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numericValue)
    },
    statusLabel(status) {
      return {
        ready: 'Ready',
        missing: 'Missing',
        not_configured: 'Not Configured'
      }[status] || status
    },
    statusClass(status) {
      return {
        ready: 'status-ready',
        missing: 'status-missing',
        not_configured: 'status-not-configured'
      }[status] || ''
    }
  }
}
</script>

<style scoped>
.shure-monthly-page {
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.page-header h1 {
  margin-bottom: 0.35rem;
}

.page-copy {
  margin: 0;
  color: #475569;
  max-width: 46rem;
}

.page-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filter-card,
.meta-card,
.query-card,
.empty-state,
.loading-state,
.report-status-card {
  border: 1px solid #dbe3ef;
  border-radius: 1rem;
  background: #ffffff;
  box-shadow: 0 0.75rem 2rem rgba(15, 23, 42, 0.05);
}

.filter-card {
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.filter-field {
  min-width: 0;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.meta-card {
  padding: 1rem 1.15rem;
}

.meta-label {
  display: block;
  margin-bottom: 0.35rem;
  color: #64748b;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.loading-state,
.empty-state {
  padding: 1.5rem;
}

.report-status-card {
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.report-status-header {
  margin-bottom: 1rem;
}

.query-results {
  display: grid;
  gap: 1.25rem;
}

.query-card {
  overflow: hidden;
}

.query-card-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
}

.query-card-header h2 {
  margin: 0 0 0.25rem;
  font-size: 1.1rem;
}

.query-meta {
  margin: 0;
  color: #64748b;
}

.query-empty {
  padding: 1rem 1.25rem 1.25rem;
  color: #64748b;
}

.table-container {
  overflow: auto;
  max-height: 400px;
  padding: 0 1.25rem 1.25rem;
}

.status-table-container {
  max-height: none;
}

.table {
  margin-bottom: 0;
  min-width: max-content;
}

.table th {
  white-space: nowrap;
}

.table td {
  vertical-align: top;
}

.table-subtext {
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 0.2rem;
}

.table-warning {
  color: #b45309;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
}

.status-ready {
  background: #dcfce7;
  color: #166534;
}

.status-missing {
  background: #fef3c7;
  color: #92400e;
}

.status-not-configured {
  background: #fee2e2;
  color: #991b1b;
}

.actions-column {
  white-space: nowrap;
}

.actions-column .btn {
  margin-right: 0.4rem;
}

.actions-column .btn:last-child {
  margin-right: 0;
}

.is-selected {
  background: rgba(59, 130, 246, 0.08);
}

.report-detail-state {
  margin-top: 1rem;
}

code {
  font-size: 0.9em;
}

@media (max-width: 768px) {
  .page-actions {
    width: 100%;
  }

  .page-actions .btn {
    flex: 1 1 auto;
  }
}
</style>
